import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProfileCached } from '../utils/cacheService';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useLanguageDetection';
import styles from './Home.module.css';
import MatrixCanvas from '../components/HomeComponents/MatrixCanvas';
import HeroSection from '../components/HomeComponents/HeroSection';
import InstagramLogin from '../components/HomeComponents/InstagramLogin';
import ConfirmModal from '../components/HomeComponents/ConfirmModal';

const Home = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const formatNumber = useNumberFormat();

  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [isBadgesVisible, setIsBadgesVisible] = useState(false);
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [statsNumber, setStatsNumber] = useState(84693);
  const [showUsernameInput, setShowUsernameInput] = useState(false);
  const [username, setUsername] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalProfileData, setModalProfileData] = useState(null);
  const [showInstagramLogin, setShowInstagramLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // TODOS OS HOOKS DEVEM SER CHAMADOS ANTES DE QUALQUER RETURN
  useEffect(() => {
    const trialActive = localStorage.getItem("trial_active");
    const trialExpires = localStorage.getItem("trial_expires");

    if (trialActive && trialExpires) {
      if (Date.now() >= parseInt(trialExpires, 10)) {
        navigate("/cta");
      }
    }
  }, [navigate]);

  // ✅ Animação rápida em cascata
  useEffect(() => {
    const timer1 = setTimeout(() => setIsButtonVisible(true), 200);
    const timer2 = setTimeout(() => setIsBadgesVisible(true), 350);
    const timer3 = setTimeout(() => setIsStatsVisible(true), 500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // Animar número de perfis
  useEffect(() => {
    if (!isStatsVisible) return;

    const savedValue = localStorage.getItem('stats_number');
    let current = savedValue ? parseInt(savedValue) : 84693;
    
    if (!savedValue) {
      localStorage.setItem('stats_number', current.toString());
    }

    setStatsNumber(current);

    const interval = setInterval(() => {
      const increment = Math.floor(Math.random() * 21) + 11;
      current += increment;
      localStorage.setItem('stats_number', current.toString());
      setStatsNumber(current);
    }, 1000);

    return () => clearInterval(interval);
  }, [isStatsVisible]);

  // Dia da semana localizado conforme idioma atual
  const localeMap = { pt: 'pt-BR', es: 'es-ES', en: 'en-US' };
  const dayOfWeek = new Intl.DateTimeFormat(localeMap[i18n.language] || 'pt-BR', { weekday: 'long' }).format(new Date());

  const handleEspionarClick = () => {
    setShowUsernameInput(true);
  };

  const handleUsernameSubmit = async () => {
    const cleanUsername = username.trim().replace(/^@+/, '');

    if (cleanUsername.length < 3) {
      alert(t('home.invalidUsername'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await fetchProfileCached(cleanUsername);
      const { success, data } = result || {};

      if (!success) throw new Error('Erro na API');
      
      if (!success || !data || !data.profile) {
        throw new Error('Dados inválidos da API');
      }

      const profile = data.profile;
      // Profile pic já vem com proxy da API
      const profileImageUrl = profile.profile_pic_url || `https://i.pravatar.cc/150?u=${cleanUsername}`;

      const profileData = {
        profileImageUrl: profileImageUrl,
        fullName: profile.full_name || profile.username || cleanUsername,
        bio: '', // API não retorna bio no endpoint search-profile
        postCount: profile.posts_count || 0,
        followersCount: profile.followers || 0,
        followingCount: profile.following || 0,
        is_private: profile.is_private || false,
        is_verified: profile.is_verified || false,
        external_link: null,
        fromApi: true,
        username: profile.username
      };

      setModalProfileData(profileData);

      // Salvar no localStorage
      const savedUsers = JSON.parse(localStorage.getItem('searched_users') || '[]');
      const alreadyExists = savedUsers.some(u => u.username === cleanUsername);
      if (!alreadyExists) {
        savedUsers.push({
          username: cleanUsername,
          ...profileData,
          searchedAt: Date.now()
        });
        localStorage.setItem('searched_users', JSON.stringify(savedUsers));
      }

      setShowConfirmModal(true);
    } catch (error) {
      // Ignorar erro de abort (timeout esperado)
      if (error.name === 'AbortError') {
        console.warn('Timeout da API, usando dados mockados');
      } else {
        console.warn('API falhou, usando dados mockados:', error.message);
      }

      const mockProfileData = {
        profileImageUrl: `https://i.pravatar.cc/150?u=${cleanUsername}`,
        fullName: cleanUsername,
        bio: '',
        postCount: Math.floor(Math.random() * 100) + 10,
        followersCount: Math.floor(Math.random() * 10000) + 1000,
        followingCount: Math.floor(Math.random() * 500) + 50,
        is_private: Math.random() > 0.7,
        is_verified: false,
        fromApi: false
      };

      setModalProfileData(mockProfileData);
      setShowConfirmModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmModal = async () => {
    setShowConfirmModal(false);
    setShowInstagramLogin(true);

    const cleanUsername = username.trim().replace(/^@+/, '');

    localStorage.setItem('current_username', cleanUsername);

    const now = Date.now();
    localStorage.setItem('trial_start', now.toString());

    const fiveMinutes = 5 * 60 * 1000;
    const trialExpires = now + fiveMinutes;
    localStorage.setItem('trial_expires', trialExpires.toString());

    localStorage.setItem('trial_active', 'true');
    localStorage.setItem('current_profile', JSON.stringify(modalProfileData));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUsernameSubmit();
    }
  };

  return (
    <div className={styles.homePage}>
      <MatrixCanvas />
      
      {!showInstagramLogin ? (
        <>
          <HeroSection
            titleText={t('home.title')}
            subtitleText={t('home.subtitle')}
            isButtonVisible={isButtonVisible}
            isBadgesVisible={isBadgesVisible}
            username={username}
            showUsernameInput={showUsernameInput}
            isLoading={isLoading}
            onEspionarClick={handleEspionarClick}
            onUsernameChange={(e) => setUsername(e.target.value)}
            onUsernameSubmit={handleUsernameSubmit}
            onKeyPress={handleKeyPress}
          />
          
          <div className={`${styles.homeStatsContainer} ${isStatsVisible ? styles.homeVisible : ''}`}>
            <p className={styles.homeStatsText}>
              <span className={styles.homeStatsNumber}>+{formatNumber(statsNumber)}</span>{' '}
              {t('home.statsText')} ({dayOfWeek})
            </p>
          </div>
        </>
      ) : (
        <InstagramLogin
          username={username}
          onLoginComplete={() => {
            navigate('/feed');
          }}
        />
      )}
      
      <ConfirmModal
        showConfirmModal={showConfirmModal}
        username={username}
        modalProfileData={modalProfileData}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmModal}
      />
    </div>
  );
};

export default Home;